package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.availability.GetAvailabilitySharingUseCase
import com.evoapps.evoshift.domain.usecase.availability.SetAvailabilitySharingUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

data class AvailabilitySharingUiState(
    val isLoading: Boolean = true,
    /** "Share my availability with all employers". */
    val shareWithAll: Boolean = false,
    /** How many employers (owners) the person works for: the choice matters from two. */
    val employers: Int = 0,
    /** Never asked and working for two or more employers: ask once. */
    val shouldAsk: Boolean = false,
    val error: UiText? = null
) {
    val offersChoice: Boolean get() = employers >= 2
}

/**
 * SH-135: whether the person's availability goes to every employer. Workplaces of the same owner
 * always share it; the switch (Profile) and the one-time question (Availability) are about other
 * employers. Optimistic: the switch moves at once and rolls back if saving fails.
 */
class AvailabilitySharingViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getSharing: GetAvailabilitySharingUseCase,
    private val setSharing: SetAvailabilitySharingUseCase,
    /** "YYYY-MM" of this month: months from it already given follow a change. */
    private val currentMonth: () -> String = {
        AppClock.todayIn(TimeZone.currentSystemDefault()).toString().take(7)
    }
) : ViewModel() {
    private val _state = MutableStateFlow(AvailabilitySharingUiState())
    val state: StateFlow<AvailabilitySharingUiState> = _state.asStateFlow()
    private val job = JobSlot()
    private var userId: String? = null

    init {
        job.launch(viewModelScope) {
            getCurrentUserUseCase()
                .map { user -> user?.let { it.uid to it.engagements.values.filter { e -> e.isActive }.map { e -> e.workplaceId }.toSet() } }
                .distinctUntilChanged()
                .collect { me ->
                    userId = me?.first
                    if (me == null) {
                        _state.value = AvailabilitySharingUiState(isLoading = false)
                        return@collect
                    }
                    getSharing(me.first).fold(
                        onSuccess = { s ->
                            _state.value = AvailabilitySharingUiState(
                                isLoading = false, shareWithAll = s.sharing.sharesWithAll, employers = s.employers, shouldAsk = s.shouldAsk
                            )
                        },
                        onFailure = { e -> _state.update { it.copy(isLoading = false, error = e.toUiText()) } }
                    )
                }
        }
    }

    /** The switch, or the answer to the question (either way it is not asked again). */
    fun set(shareWithAll: Boolean) {
        val uid = userId ?: return
        val before = _state.value
        _state.update { it.copy(shareWithAll = shareWithAll, shouldAsk = false, error = null) }
        viewModelScope.launch {
            setSharing(uid, shareWithAll, currentMonth()).onFailure { e ->
                _state.update { it.copy(shareWithAll = before.shareWithAll, shouldAsk = before.shouldAsk, error = e.toUiText()) }
            }
        }
    }

    fun messageShown() = _state.update { it.copy(error = null) }
}
